// One URL, two states: the tool for a signed-in user, its landing view
// (src/pages/GuestLanding.vue) for everyone else.
//
// Why not a separate marketing URL: /g25 and /calculators are the addresses
// that already rank and already have the links. Until now a signed-out
// visitor arriving from search was redirected to "/" with the sign-in modal
// over it, while the prerender Lambda showed crawlers the tool itself; the
// visitor never saw what they had searched for, and search engines and
// people were shown different things. Rendering the landing on the same URL
// fixes both. The prerenderer always gets the landing.
//
// The first render is decided the way the router guard decides it, because
// nothing better exists before Amplify is configured: the store's user, else
// the Cognito LastAuthUser hint. After that the gate only moves on two
// signals, never on the hint alone:
//   - to the landing on `authState: signedOut`, which also covers a stale
//     hint (Nav's currentAuthenticatedUser() catch signs out);
//   - to the tool when Nav has finished signing someone in on this URL
//     (`appEvents: showTool`, sent after the user and subscription are
//     loaded). Swapping earlier, on the `signedIn` event or the storage key,
//     mounts the workbench before the store has a user: g25 and admixCalc
//     only run init() at mount when there is one, and nothing re-runs it,
//     so the visitor would sign in to an empty workbench.
//
// Both halves are async components: a visitor who never signs in does not
// download the workbench up front, and a signed-in user never downloads the
// landing.
//
// The gate is the route's component now, so it stands in for the tool in
// two ways the router relies on:
//   - in-component guards (beforeRouteLeave/Update) are only called on the
//     route's own component, so the gate forwards them to the tool. Family
//     Tree saves unsaved work in its beforeRouteLeave; without this the
//     save would silently stop running.
//   - an entitlement rule (meta.rule, Admix+) is checked by postAuth on
//     navigation, and an in-place sign-in on the landing is no navigation,
//     so showTool checks it the same way before mounting the tool.
import { defineAsyncComponent, h } from 'vue'
import AmplifyEventBus from '@/plugins/aws-amplify-vue/events/AmplifyEventBus'
import { store } from '@/Store'
import { createCanAccess } from '@/entitlements'
import { isPrerender } from '@/utils/isPrerender'
import { hasSignInHint, titleForEnv } from '@/utils/guestLanding'

const canAccess = createCanAccess(store)

// Run the tool's own guard (options API: a function, or an array once
// mixins merge) with the tool as `this`, stopping at the first answer that
// is not "go ahead".
async function forwardGuard(instance, name, to, from) {
    const guard = instance && instance.$options && instance.$options[name]
    if (!guard) return true
    for (const fn of Array.isArray(guard) ? guard : [guard]) {
        const result = await fn.call(instance, to, from)
        if (result !== undefined && result !== true) return result
    }
    return true
}

const GuestLanding = defineAsyncComponent(() => import('@/pages/GuestLanding.vue'))

export function guestGate(loadTool, landing) {
    const Tool = defineAsyncComponent(loadTool)
    return {
        name: 'GuestGate',
        inheritAttrs: false,
        data() {
            return { showLanding: this.decide() }
        },
        watch: {
            // The landing titles itself when it mounts (the title search
            // shows); swapping to the tool puts back the workbench's name,
            // which the router guard set from meta.title on arrival.
            showLanding(landingShown) {
                const title = this.$route && this.$route.meta && this.$route.meta.title
                if (!landingShown && title) document.title = titleForEnv(this.$store, title)
            },
        },
        created() {
            this.onAuthState = info => {
                if (info === 'signedOut') this.showLanding = true
            }
            this.onAppEvent = info => {
                if (!(info && typeof info === 'object' && info.component === 'guestGate' && info.exec === 'showTool')) return
                const landingShown = this.decide()
                // Signed in but not entitled to this tool: leave the way
                // postAuth would have on a navigation.
                if (!landingShown && !this.entitled()) {
                    this.$router.replace('/')
                    return
                }
                this.showLanding = landingShown
            }
            AmplifyEventBus.on('authState', this.onAuthState)
            AmplifyEventBus.on('appEvents', this.onAppEvent)
        },
        beforeUnmount() {
            AmplifyEventBus.off('authState', this.onAuthState)
            AmplifyEventBus.off('appEvents', this.onAppEvent)
        },
        beforeRouteLeave(to, from) {
            return this.showLanding ? true : forwardGuard(this.$refs.tool, 'beforeRouteLeave', to, from)
        },
        beforeRouteUpdate(to, from) {
            return this.showLanding ? true : forwardGuard(this.$refs.tool, 'beforeRouteUpdate', to, from)
        },
        methods: {
            entitled() {
                const rule = this.$route && this.$route.meta && this.$route.meta.rule
                if (!rule || !this.$store.getters.get('SET_ENTITLEMENTS_READY')) return true
                return canAccess(rule)
            },
            decide() {
                if (isPrerender()) return true
                const user = this.$store.getters.get('user')
                if (user && user.username) return false
                return !hasSignInHint()
            },
        },
        render() {
            return this.showLanding
                ? h(GuestLanding, { landing })
                // The ref reaches the loaded tool: defineAsyncComponent
                // hands a wrapper's ref on to the inner component.
                : h(Tool, { ...this.$attrs, ref: 'tool' })
        },
    }
}

export default guestGate
